import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList, ResponsiveContainer,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { GraduationCap, LogOut, AlertTriangle, Pencil } from "lucide-react";
import { getFormerStudentsDashboard, updateStudent } from "../../lib/api";
import { EXIT_REASONS, OTHER_EXIT_REASON, exitReasonLabel } from "../../lib/exitReasons";

const SELF_OTHER = "Self / Independent / Other";
// Distinct colors per academic year for the stacked bars.
const YEAR_PALETTE = ["#00305E", "#2563eb", "#16a34a", "#f59e0b", "#8b5cf6", "#dc2626", "#0891b2", "#65a30d", "#db2777", "#475569"];

const yearLabel = (y) => (y || "").replace("-", "\u2013");
const truncate = (s, n = 18) => (s && s.length > n ? `${s.slice(0, n)}\u2026` : s || "");

const wrapLabel = (s, maxChars = 28, maxLines = 2) => {
  const words = String(s || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";
  for (const w of words) {
    if (!line) { line = w; }
    else if ((line + " " + w).length <= maxChars) { line += " " + w; }
    else { lines.push(line); line = w; }
    if (lines.length === maxLines - 1 && line.length > maxChars) break;
  }
  if (line) lines.push(line);
  if (lines.length > maxLines) {
    const kept = lines.slice(0, maxLines);
    kept[maxLines - 1] = truncate(kept[maxLines - 1], maxChars - 1);
    return kept;
  }
  return lines;
};

const DistrictTick = ({ x, y, payload }) => {
  const lines = wrapLabel(payload?.value);
  const dy = -((lines.length - 1) * 6);
  return (
    <text x={x} y={y} dy={dy} textAnchor="end" fontSize={12} fill="#334155">
      {lines.map((ln, i) => (
        <tspan key={i} x={x} dx={-6} dy={i === 0 ? 4 : 13}>{ln}</tspan>
      ))}
    </text>
  );
};

// Shared dialog to set/edit a student's Exit Reason + Departure Notes.
function StudentExitEditor({ open, target, onOpenChange, onSaved }) {
  const [exitReason, setExitReason] = useState("");
  const [exitReasonOther, setExitReasonOther] = useState("");
  const [departureNotes, setDepartureNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && target) {
      setExitReason(target.exit_reason || "");
      setExitReasonOther(target.exit_reason_other || "");
      setDepartureNotes(target.departure_notes || "");
    }
  }, [open, target]);

  const isOther = exitReason === OTHER_EXIT_REASON;
  const canSave = !!exitReason && (!isOther || exitReasonOther.trim().length > 0);

  const save = async () => {
    setSaving(true);
    try {
      await updateStudent(target.student_id, {
        exit_reason: exitReason,
        exit_reason_other: isOther ? exitReasonOther.trim() : "",
        departure_notes: departureNotes,
      });
      toast.success("Exit details saved");
      onSaved();
      onOpenChange(false);
    } catch {
      toast.error("Could not save exit details");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="student-exit-editor">
        <DialogHeader>
          <DialogTitle>Exit Details — {target?.name}</DialogTitle>
          <DialogDescription>Set the exit reason and any departure notes. This updates the student record and syncs everywhere.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Exit Reason</Label>
            <Select value={exitReason} onValueChange={(v) => { setExitReason(v); if (v !== OTHER_EXIT_REASON) setExitReasonOther(""); }}>
              <SelectTrigger data-testid="editor-exit-reason-select"><SelectValue placeholder="Select an exit reason…" /></SelectTrigger>
              <SelectContent>
                {EXIT_REASONS.map((r) => (
                  <SelectItem key={r} value={r} data-testid={`editor-exit-reason-${r}`}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isOther && (
              <Input
                placeholder="Please specify the reason"
                value={exitReasonOther}
                data-testid="editor-exit-reason-other"
                onChange={(e) => setExitReasonOther(e.target.value)}
              />
            )}
          </div>
          <div className="space-y-2">
            <Label>Departure Notes</Label>
            <Textarea
              placeholder="Any additional context about this student's departure"
              rows={4}
              value={departureNotes}
              data-testid="editor-departure-notes"
              onChange={(e) => setDepartureNotes(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={!canSave || saving} data-testid="editor-save">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// One Graduates / Disenrolled sub-section: district-by-year table + stacked bar chart.
function ExitGroup({ testid, icon: Icon, tint, title, description, group, years, onDrill, reasonBreakdown }) {
  const rows = group?.rows || [];
  const totals = group?.totals || { per_year: {}, total: 0 };
  const isEmpty = rows.length === 0;

  const chartData = useMemo(
    () => rows.map((r) => ({ district: r.district, ...r.per_year })),
    [rows]
  );

  return (
    <Card data-testid={testid}>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full ${tint}`}>
            <Icon className="h-4 w-4" />
          </span>
          {title}
          <span className="ml-1 text-sm font-normal text-muted-foreground">({totals.total})</span>
        </CardTitle>
        <CardDescription>{description} — click a district row to see the students behind the numbers</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6 overflow-x-auto">
        {isEmpty ? (
          <p className="text-sm text-muted-foreground py-8 text-center">No students in this category yet.</p>
        ) : (
          <>
            <Table data-testid={`${testid}-table`}>
              <TableHeader>
                <TableRow>
                  <TableHead className="border-r">Sending District</TableHead>
                  {years.map((y) => (
                    <TableHead key={y} className="text-center">{yearLabel(y)}</TableHead>
                  ))}
                  <TableHead className="text-center font-semibold border-l">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.district}
                    data-testid={`${testid}-row-${row.district}`}
                    className="cursor-pointer hover:bg-slate-50 print:cursor-auto"
                    onClick={() => onDrill(row.district)}
                  >
                    <TableCell className="font-medium whitespace-nowrap border-r text-blue-700 hover:underline print:text-slate-800 print:no-underline">{row.district}</TableCell>
                    {years.map((y) => (
                      <TableCell key={y} className="text-center">{row.per_year[y] || 0}</TableCell>
                    ))}
                    <TableCell className="text-center font-semibold border-l">{row.total}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-[#00305E]/5 font-bold" data-testid={`${testid}-totals-row`}>
                  <TableCell className="border-r">Total / All Districts</TableCell>
                  {years.map((y) => (
                    <TableCell key={y} className="text-center">{totals.per_year[y] || 0}</TableCell>
                  ))}
                  <TableCell className="text-center border-l">{totals.total}</TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <ResponsiveContainer width="100%" height={Math.max(280, chartData.length * 38)}>
              <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 24, top: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} fontSize={11} />
                <YAxis type="category" dataKey="district" width={220} fontSize={12} interval={0} tick={<DistrictTick />} />
                <Tooltip labelFormatter={(v) => v} />
                <Legend />
                {years.map((y, i) => (
                  <Bar key={y} dataKey={y} name={yearLabel(y)} stackId="a" fill={YEAR_PALETTE[i % YEAR_PALETTE.length]} />
                ))}
              </BarChart>
            </ResponsiveContainer>

            {reasonBreakdown && reasonBreakdown.length > 0 && (
              <div className="pt-4 border-t" data-testid={`${testid}-reason-breakdown`}>
                <h4 className="text-sm font-semibold text-slate-700 mb-1">By Exit Reason</h4>
                <p className="text-xs text-muted-foreground mb-3">How the disenrolled total splits across the individual exit reasons</p>
                <ResponsiveContainer width="100%" height={Math.max(220, reasonBreakdown.length * 42)}>
                  <BarChart data={reasonBreakdown} layout="vertical" margin={{ left: 8, right: 36, top: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} fontSize={11} />
                    <YAxis type="category" dataKey="reason" width={220} fontSize={12} interval={0} tick={<DistrictTick />} />
                    <Tooltip labelFormatter={(v) => v} />
                    <Bar dataKey="count" name="Students" fill="#e11d48" radius={[0, 4, 4, 0]}>
                      <LabelList dataKey="count" position="right" fontSize={11} fill="#475569" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function FormerStudentsSection({ readOnly = false }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null);   // { title, rows }
  const [editor, setEditor] = useState(null); // student record being edited

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getFormerStudentsDashboard();
      setData(res.data);
    } catch {
      toast.error("Failed to load the Former Students breakdown");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDrill = (groupKey, groupTitle, district) => {
    const rows = (data?.[groupKey]?.records || []).filter((r) => r.district === district);
    setDrill({ title: `${groupTitle} — ${district}`, rows });
  };

  const afterSave = () => { setDrill(null); load(); };

  if (loading) {
    return <div className="py-10 text-center text-muted-foreground" data-testid="former-students-loading">Loading former students…</div>;
  }
  if (!data) return null;

  const { academic_years: years = [], graduates, disenrolled, needs_review = [] } = data;

  // Split the disenrolled total across the 11 non-graduate exit reasons.
  const disenrolledBreakdown = EXIT_REASONS
    .filter((r) => r !== "Graduated")
    .map((reason) => ({
      reason,
      count: (disenrolled?.records || []).filter((x) => (x.exit_reason || "") === reason).length,
    }))
    .filter((b) => b.count > 0)
    .sort((a, b) => b.count - a.count);

  return (
    <div className="space-y-6" data-testid="former-students-section">
      <div>
        <h2 className="text-lg font-semibold text-slate-800">Former Students</h2>
        <p className="text-xs text-muted-foreground">Students who have left, split by exit reason and grouped by Sending District &amp; academic year of exit</p>
      </div>

      {/* Needs-review flag: former students with no Exit Reason set */}
      {needs_review.length > 0 && (
        <Card className="border-amber-300 bg-amber-50/60 print:hidden" data-testid="exit-reason-review-banner">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-amber-800">
              <AlertTriangle className="h-4 w-4" /> {needs_review.length} former student{needs_review.length === 1 ? "" : "s"} need{needs_review.length === 1 ? "s" : ""} an Exit Reason
            </CardTitle>
            <CardDescription>These students left without an exit reason on file and aren&apos;t counted below yet. Set one for each so they appear in the Graduates or Disenrolled totals.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {needs_review.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-3 py-1.5 border-b border-amber-200 last:border-0" data-testid={`exit-review-row-${r.id}`}>
                <span className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle className="h-3.5 w-3.5" /></span>
                <span className="font-medium text-sm min-w-[140px]">{r.name}</span>
                <span className="text-xs text-muted-foreground flex-1">
                  {r.exit_year && r.exit_year !== "Unknown" ? `Left ${yearLabel(r.exit_year)}` : "Exit date unknown"}
                  {r.leaving_reason ? ` · Reason on file: ${r.leaving_reason}` : ""}
                </span>
                {!readOnly && (
                  <Button size="sm" variant="outline" className="h-8 bg-white" onClick={() => setEditor(r)} data-testid={`set-exit-reason-${r.id}`}>
                    Set exit reason
                  </Button>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <ExitGroup
        testid="graduates-group"
        icon={GraduationCap}
        tint="bg-emerald-50 text-emerald-600"
        title="Graduates"
        description="Students who left because they completed the program or aged out"
        group={graduates}
        years={years}
        onDrill={(district) => openDrill("graduates", "Graduates", district)}
      />
      <ExitGroup
        testid="disenrolled-group"
        icon={LogOut}
        tint="bg-rose-50 text-rose-600"
        title="Disenrolled"
        description="Students who left for any other reason"
        group={disenrolled}
        years={years}
        onDrill={(district) => openDrill("disenrolled", "Disenrolled", district)}
        reasonBreakdown={disenrolledBreakdown}
      />

      {/* Drill-down: the individual students behind a district row */}
      <Dialog open={!!drill} onOpenChange={(o) => !o && setDrill(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle data-testid="former-drill-title">{drill?.title}</DialogTitle>
            <DialogDescription>
              {drill?.rows?.length || 0} student{(drill?.rows?.length || 0) === 1 ? "" : "s"} on record
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {(drill?.rows?.length || 0) === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">No students found.</p>
            ) : (
              <Table data-testid="former-drill-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Student</TableHead>
                    <TableHead>Year of Exit</TableHead>
                    <TableHead>Exit Reason</TableHead>
                    <TableHead>Departure Notes</TableHead>
                    {!readOnly && <TableHead className="text-right">Edit</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(drill?.rows || []).map((r, i) => (
                    <TableRow key={`${r.student_id || r.name}-${i}`} data-testid={`former-drill-row-${i}`}>
                      <TableCell className="font-medium">
                        {r.student_id ? (
                          <button className="text-blue-700 hover:underline" onClick={() => navigate(`/students/${r.student_id}`)}>
                            {r.name}
                          </button>
                        ) : r.name}
                      </TableCell>
                      <TableCell>{r.academic_year && r.academic_year !== "Unknown" ? yearLabel(r.academic_year) : "Unknown"}</TableCell>
                      <TableCell className="text-sm">{exitReasonLabel(r.exit_reason, r.exit_reason_other) || "—"}</TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[220px] truncate" title={r.departure_notes || ""}>{r.departure_notes || "—"}</TableCell>
                      {!readOnly && (
                        <TableCell className="text-right">
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditor(r)} data-testid={`edit-exit-${r.student_id}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <StudentExitEditor
        open={!!editor}
        target={editor}
        onOpenChange={(o) => !o && setEditor(null)}
        onSaved={afterSave}
      />
    </div>
  );
}
